import { type Vistoria, estadoFitoVistoriaConfig, riscoVistoriaConfig } from "@/mocks/vistorias";
import InspectionStatusBadge from "./InspectionStatusBadge";
import InspectionTypeBadge from "./InspectionTypeBadge";

interface InspectionTimelineProps {
  vistorias: Vistoria[];
  onSelect: (v: Vistoria) => void;
}

export default function InspectionTimeline({ vistorias, onSelect }: InspectionTimelineProps) {
  if (vistorias.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-background-200/70 p-12 text-center">
        <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-history-line text-foreground-400 text-2xl"></i>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 mb-1">Nenhuma vistoria encontrada</h3>
        <p className="text-sm text-foreground-500">Tente ajustar os filtros ou limpar a busca.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {vistorias.map((v, idx) => {
        const fitoConfig = estadoFitoVistoriaConfig[v.estadoFitossanitario] || { badge: "", label: v.estadoFitossanitario };
        const riscoConfig = riscoVistoriaConfig[v.classeRisco] || { badge: "", label: v.classeRisco };
        const dotColorMap: Record<string, string> = {
          bom: "bg-primary-500",
          regular: "bg-caution-500",
          ruim: "bg-warning-500",
          critico: "bg-danger-500",
          morto: "bg-foreground-400",
        };
        const lineColor = idx < vistorias.length - 1 ? "bg-background-200" : "";

        return (
          <div key={v.id} className="flex gap-4">
            {/* Timeline dot & line */}
            <div className="flex flex-col items-center shrink-0">
              <div className={`w-3 h-3 rounded-full ${dotColorMap[v.estadoFitossanitario] || "bg-foreground-400"} mt-1.5 shrink-0`}></div>
              {lineColor && <div className={`w-0.5 flex-1 ${lineColor}`}></div>}
            </div>

            {/* Card */}
            <div
              className="flex-1 bg-white rounded-xl border border-background-200/70 p-4 hover:border-background-300/80 transition-colors cursor-pointer mb-0.5"
              onClick={() => onSelect(v)}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-xs text-accent-600 font-medium whitespace-nowrap">{v.codigoCta}</span>
                  <span className="text-foreground-400 text-xs hidden sm:inline">•</span>
                  <span className="text-sm font-semibold text-foreground-900 truncate">{v.nomePopular}</span>
                </div>
                <InspectionStatusBadge status={v.status} />
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                <InspectionTypeBadge type={v.tipo} />
                <span className={`badge ${fitoConfig.badge}`}>{fitoConfig.label}</span>
                <span className={`badge ${riscoConfig.badge}`}>{riscoConfig.label}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <div>
                  <span className="text-xs text-foreground-400">Data</span>
                  <p className="text-xs font-medium text-foreground-700">{new Date(v.data + "T00:00:00").toLocaleDateString("pt-BR")}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-400">Técnico</span>
                  <p className="text-xs font-medium text-foreground-700">{v.tecnico}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-400">DAP</span>
                  <p className="text-xs font-medium text-foreground-700">{v.dap} cm</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-400">Altura</span>
                  <p className="text-xs font-medium text-foreground-700">{v.altura} m</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-camera-line"></i> {v.fotos}
                  </span>
                  <span className="hidden sm:inline">{v.bairro}</span>
                </div>
                <span className="text-xs text-primary-600 font-medium cursor-pointer hover:text-primary-700">
                  Ver detalhes <i className="ri-arrow-right-line ml-0.5"></i>
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}